import { call } from 'redux-saga/effects';
import i18next from 'i18next';
import ddt from '@wix/da-ddt';
import { requestPuppy } from '@wix/da-http-client';
import { FolderType, GroupFolderId } from './types';
import {
  PapiRequestDaDeviationGroupFolders,
  PapiDaDeviationGroupFolders,
  PapiGallection,
  PapiRequestDaDeviationGroupAdd,
  PapiDaDeviationGroupAdd,
  PapiDaSharedNetworkbar,
  PapiUser,
} from '@wix/da-papi-types';
import { putErrorNotification } from '../../utils/saga';
import { validateGroupAddRequest } from '../../utils/validation/validateGroupAddRequest';

const logger = ddt.createLogger('AddToGroupsPanel');

export function* fetchGroupFolders(groupId: number, folderType: FolderType) {
  const params: PapiRequestDaDeviationGroupFolders = {
    groupid: groupId,
    type: folderType,
  };

  const result: PapiDaDeviationGroupFolders | false = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'group_folders',
      params,
    },
    (): PapiDaDeviationGroupFolders => {
      if (process.env.NODE_ENV !== 'production') {
        const collections: PapiGallection[] =
          // eslint-disable-next-line global-require
          require('@wix/da-sample-data').collections;

        return {
          results: collections,
        };
      }
      return {
        results: [],
      };
    },
    'dadeviation'
  );

  return result;
}

export function* addDeviationToGroupFolder(
  groupFolderId: GroupFolderId,
  deviationId: number
) {
  const data: PapiRequestDaDeviationGroupAdd = {
    groupid: groupFolderId.groupId,
    type: groupFolderId.type,
    folderid: groupFolderId.folderId,
    deviationid: deviationId,
  };

  // Validate request data before making API call
  if (!validateGroupAddRequest(data)) {
    const errorDetails = i18next.t('common.error.invalidRequest');
    yield putErrorNotification(errorDetails);
    logger.error('Group add validation failed:', { data });
    return {
      success: false,
      errorDetails,
    };
  }

  const result: PapiDaDeviationGroupAdd = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'group_add',
      data,
    },
    (): PapiDaDeviationGroupAdd => {
      return { success: true };
    },
    'dadeviation'
  ) || { success: false };

  return result;
}

function getFakeRecommendedGroups(cursor?: string) {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line global-require
    const groups: PapiUser[] = require('@wix/da-sample-data').groups;
    const offset = Number(cursor ?? 0);

    const recommendedGroups = {
      groups: groups
        .slice(offset, offset + 3)
        .map((group, idx) => ({
          ...group,
          userId: group.userId + 10000000,
        }))
        .map(group => ({ ...group, isWatching: false })),
      hasMore: false,
      cursor: offset + 3,
    };

    return {
      recommendedGroups,
    };
  }

  return {};
}

export function* fetchRecommendedGroups(cursor?: string) {
  const result: PapiDaSharedNetworkbar | false = yield call(
    requestPuppy,
    {
      method: 'get',
      url: '/networkbar',
      params: {
        cursor,
        content_type: 'recommended_groups',
        expand: 'user.networkbar,user.watch',
      },
    },
    () => getFakeRecommendedGroups(cursor)
  );

  return result;
}
